import {
  Box,
  Stack,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
} from '@mui/material';
import { IconCheck, IconInfoCircle } from '@tabler/icons-react';
import { CurrentSubscription } from '@app/types/payment/subscription';
import { formatSubscriptionPriceCurrency } from '@utils/utils';

interface PlanDetailsCardProps {
  currentSubscription: CurrentSubscription | null;
  casesLimit?: number;
  planFeatures?: string[];
}

function PlanDetailsCard({
  currentSubscription,
  casesLimit,
  planFeatures,
}: PlanDetailsCardProps) {
  const theme = useTheme();

  if (!currentSubscription) {
    return (
      <Box
        sx={{
          p: 3,
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          textAlign: 'center',
        }}
      >
        <IconInfoCircle size={48} color={theme.palette.text.secondary} />
        <Typography variant="body2" color="text.secondary" mt={2}>
          No active plan. Please select a plan to view details.
        </Typography>
      </Box>
    );
  }

  const {
    subscription_name,
    description,
    price,
    period,
    is_trial,
    days_remaining,
  } = currentSubscription;

  const isFree = price === '0.00';

  return (
    <Box
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={3}>
        {/* Plan Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              {subscription_name}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
          {is_trial && (
            <Chip
              label={`Trial: ${days_remaining} days left`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
                fontWeight: 600,
              }}
            />
          )}
        </Stack>

        {/* Pricing */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={0.5}
          >
            Pricing
          </Typography>
          {isFree ? (
            <Typography variant="h5" color="success.main">
              Free
            </Typography>
          ) : (
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="h5" fontWeight={600}>
                {formatSubscriptionPriceCurrency(Number(price))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                / {period?.toLowerCase() || 'month'}
              </Typography>
            </Stack>
          )}
        </Box>

        {/* Cases Limit */}
        {casesLimit !== undefined && casesLimit > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Case Limit
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body1" fontWeight={500}>
                {casesLimit} {casesLimit === 1 ? 'case' : 'cases'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                per month
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Plan Features */}
        {planFeatures && planFeatures.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={1}
            >
              Features
            </Typography>
            <List dense disablePadding>
              {planFeatures.map((feature, index) => (
                <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <IconCheck size={18} color={theme.palette.success.main} />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{
                      variant: 'body2',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Trial Info */}
        {is_trial && (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <IconInfoCircle size={20} color={theme.palette.info.main} />
              <Box>
                <Typography variant="body2" color="info.main" fontWeight={600}>
                  Free Trial Active
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Your card will be charged automatically when the trial ends in{' '}
                  {days_remaining} {days_remaining === 1 ? 'day' : 'days'}.
                  Cancel anytime before then to avoid charges.
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export default PlanDetailsCard;
